import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ACTIONS, allowedForIndustry, EXTRA_PERMISSIONS, MODULES, type RoleDetail } from '@kontor/shared';
import { api, ApiError } from '../api';
import { useSession } from '../session';

const ACTION_LABELS: Record<(typeof ACTIONS)[number], string> = { read: 'Ansehen', write: 'Anlegen und bearbeiten', delete: 'Löschen' };

interface Draft {
  /** null = neue Rolle */
  key: string | null;
  name: string;
  permissions: Set<string>;
}

/**
 * Bereich „Rollen“ der Einrichtung (KON-073). Der Inhaber legt Rollen an, benennt sie um, löscht sie und bestimmt je Modul,
 * was die Rolle darf. Alle anderen mit Leserecht sehen die Rollen nur. Was erlaubt ist, entscheidet der Server.
 */
export function RolesPage() {
  const { session, hasModule } = useSession();
  const client = useQueryClient();
  const isOwner = session?.user.role.key === 'owner';
  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const roles = useQuery({ queryKey: ['role-details'], queryFn: async () => (await api<{ data: RoleDetail[] }>('GET', '/roles')).data });
  const refresh = () => {
    client.invalidateQueries({ queryKey: ['role-details'] });
    client.invalidateQueries({ queryKey: ['roles'] });
  };
  // Nur Module, die das Paket enthält und die es schon gibt
  const modules = MODULES.filter((m) => m.available && hasModule(m.key));

  const save = useMutation({
    mutationFn: (d: Draft) => {
      const body = { name: d.name.trim(), permissions: [...d.permissions] };
      return d.key === null ? api('POST', '/roles', body) : api('PATCH', `/roles/${d.key}`, body);
    },
    onSuccess: () => {
      setDraft(null);
      setErrors({});
      setFormError(null);
      refresh();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setFormError(e.fields.permissions ?? null);
      } else {
        setFormError(e instanceof ApiError ? e.message : 'Die Rolle konnte nicht gespeichert werden.');
      }
    },
  });
  const remove = useMutation({
    mutationFn: (key: string) => api('DELETE', `/roles/${key}`),
    onSuccess: refresh,
  });

  const start = (role: RoleDetail | null) => {
    setErrors({});
    setFormError(null);
    setDraft(role ? { key: role.key, name: role.name, permissions: new Set(role.permissions) } : { key: null, name: '', permissions: new Set() });
  };
  /** Anlegen und Löschen setzen das Ansehen voraus, ohne Ansehen ist die Rolle in dem Modul wirkungslos */
  const toggleAction = (module: string, action: (typeof ACTIONS)[number], on: boolean) =>
    setDraft((d) => {
      if (!d) return d;
      const next = new Set(d.permissions);
      const key = `${module}.${action}`;
      if (on) {
        next.add(key);
        if (action !== 'read') next.add(`${module}.read`);
      } else {
        next.delete(key);
        if (action === 'read') {
          next.delete(`${module}.write`);
          next.delete(`${module}.delete`);
          // Zusätzliche Rechte setzen das Ansehen voraus
          for (const extra of EXTRA_PERMISSIONS) if (extra.module === module && !('standalone' in extra && extra.standalone)) next.delete(extra.key);
        }
      }
      return { ...d, permissions: next };
    });

  // Zusatzrechte, die nur für eine andere Branche gelten (zum Beispiel „Fahrten annehmen“ im Chauffeur-Service), erscheinen nicht
  const industry = session?.tenant.industry ?? null;
  const extrasOf = (module: string) => EXTRA_PERMISSIONS.filter((e) => e.module === module && allowedForIndustry('industries' in e ? e.industries : undefined, industry));
  const hasExtras = modules.some((m) => extrasOf(m.key).length > 0);
  const error = [remove.error, roles.error].find((e) => e instanceof ApiError) as ApiError | undefined;
  const list = roles.data ?? [];

  const editor = draft && (
    <form
      className="role-editor"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(draft);
      }}
      noValidate
    >
      <h3>{draft.key === null ? 'Neue Rolle' : `Rolle „${list.find((r) => r.key === draft.key)?.name ?? ''}“ bearbeiten`}</h3>
      {formError && <div className="alert alert--error" role="alert">{formError}</div>}
      <label className="field">
        <span>Bezeichnung <abbr title="Pflichtfeld">*</abbr></span>
        <input className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} maxLength={60} autoComplete="off" aria-invalid={!!errors.name} autoFocus />
        {errors.name && <small className="field__error" role="alert">{errors.name}</small>}
      </label>

      <fieldset className="matrix">
        <legend>Rechte</legend>
        <p className="muted small">Wählen Sie je Modul, was die Rolle darf. Anlegen und Löschen setzen das Ansehen voraus. Es erscheinen nur Module, die Ihr Paket enthält.</p>
        <div className="matrix__scroll">
          <table className="table matrix__table">
            <thead>
              <tr>
                <th>Modul</th>
                {ACTIONS.map((a) => <th key={a} className="matrix__col">{ACTION_LABELS[a]}</th>)}
                {hasExtras && <th className="matrix__col">Zusätzlich</th>}
              </tr>
            </thead>
            <tbody>
              {modules.map((m) => (
                <tr key={m.key}>
                  <th scope="row" className="matrix__module">{m.label}</th>
                  {ACTIONS.map((a) => (
                    <td key={a} className="matrix__col">
                      <input type="checkbox" className="matrix__check" aria-label={`${m.label}: ${ACTION_LABELS[a]}`} checked={draft.permissions.has(`${m.key}.${a}`)} onChange={(e) => toggleAction(m.key, a, e.target.checked)} />
                    </td>
                  ))}
                  {hasExtras && (
                    <td className="matrix__col">
                      {extrasOf(m.key).map((e) => (
                        <label key={e.key} className="check" title="Sehen, aber nicht ändern: Termine und Aufgaben aller Mitarbeiter erscheinen im Kalender">
                          <input
                            type="checkbox"
                            checked={draft.permissions.has(e.key)}
                            onChange={(ev) =>
                              setDraft((d) => {
                                if (!d) return d;
                                const next = new Set(d.permissions);
                                if (ev.target.checked) {
                                  next.add(e.key);
                                  if (!('standalone' in e && e.standalone)) next.add(`${m.key}.read`);
                                } else next.delete(e.key);
                                return { ...d, permissions: next };
                              })
                            }
                          />
                          <span>{e.label}</span>
                        </label>
                      ))}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </fieldset>

      <div className="form-actions">
        <button className="btn btn--primary" type="submit" disabled={save.isPending || draft.name.trim().length < 2}>
          {save.isPending ? 'Wird gespeichert …' : draft.key === null ? 'Rolle anlegen' : 'Speichern'}
        </button>
        <button className="btn btn--secondary" type="button" onClick={() => setDraft(null)}>Abbrechen</button>
      </div>
    </form>
  );

  return (
    <section className="card">
      <div className="page-head">
        <h2>Rollen &amp; Rechte</h2>
        {isOwner && !draft && <button type="button" className="btn btn--primary btn--sm page-head__action" onClick={() => start(null)}>Neue Rolle</button>}
      </div>
      <p className="muted small">
        Eine Rolle bestimmt, was ein Benutzer in den Modulen Ihres Pakets darf. Welche Rolle ein Benutzer hat, legen Sie unter „Benutzer“ fest.
        {!isOwner && ' Rollen anlegen und ändern kann nur ein Inhaber.'}
      </p>
      {error && <div className="alert alert--error" role="alert">{error.message}</div>}

      {draft?.key === null && editor}

      {roles.isLoading && <p className="muted">Wird geladen …</p>}
      {list.map((role) => {
        const editing = draft?.key === role.key;
        return (
          <div key={role.key} className="role-block">
            <div className="role-block__head">
              <strong>{role.name}</strong>
              <span className="muted small">{role.userCount === 0 ? 'Kein Benutzer' : role.userCount === 1 ? '1 Benutzer' : `${role.userCount} Benutzer`}</span>
              {isOwner && !role.locked && (
                <span className="row-actions">
                  <button type="button" className="btn btn--secondary btn--sm" aria-expanded={editing} onClick={() => (editing ? setDraft(null) : start(role))}>{editing ? 'Zuklappen' : 'Bearbeiten'}</button>
                  <button
                    type="button"
                    className="btn btn--danger btn--sm"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm(`Rolle „${role.name}“ wirklich löschen?`)) remove.mutate(role.key);
                    }}
                  >
                    Löschen
                  </button>
                </span>
              )}
            </div>
            {editing ? (
              editor
            ) : role.locked ? (
              <p className="muted small">Der Inhaber darf alles und lässt sich nicht ändern.</p>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}
